import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { describeError } from '../../api/client'
import { useLabourCategories, useMaterials, useWorkPackages } from '../../api/hooks'
import {
  useCreateActivity,
  useDeleteActivity,
  useLinkActivity,
  useSiteDrawings,
  useUnlinkActivity,
  useUpdateActivity,
  type ActivityNode,
  type ActivityStatus,
  type DependencyLink,
  type DependencyType,
} from '../../api/network'
import { useSiteWorkItems } from '../../api/templates'

const STATUSES: ActivityStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'DELAYED', 'ON_HOLD']
const TYPES: { value: DependencyType; label: string }[] = [
  { value: 'FS', label: 'Finish → start' },
  { value: 'SS', label: 'Start → start' },
  { value: 'FF', label: 'Finish → finish' },
]

/**
 * An activity and everything it is joined to.
 *
 * <p>The four link lists are the point of the screen. An activity with none of them is a
 * box on a chart; one with its drawings, materials, trades and BOQ lines attached is a
 * question the system can answer every morning without anybody being asked.</p>
 */
export function ActivityEditorDialog({
  siteId,
  activity,
  activities,
  dependencies,
  onClose,
}: {
  siteId: string
  activity?: ActivityNode
  activities: ActivityNode[]
  dependencies: DependencyLink[]
  onClose: () => void
}) {
  const workPackages = useWorkPackages()
  const workItems = useSiteWorkItems(siteId)
  const drawings = useSiteDrawings(siteId)
  const materials = useMaterials({ page: 0, size: 300 })
  const labourCategories = useLabourCategories()

  const create = useCreateActivity(siteId)
  const update = useUpdateActivity()
  const remove = useDeleteActivity()
  const link = useLinkActivity()
  const unlink = useUnlinkActivity()

  const [code, setCode] = useState(activity?.code ?? '')
  const [name, setName] = useState(activity?.name ?? '')
  const [description, setDescription] = useState(activity?.description ?? '')
  const [workPackageId, setWorkPackageId] = useState(activity?.workPackageId ?? '')
  const [durationDays, setDurationDays] = useState(String(activity?.durationDays ?? 5))
  const [milestone, setMilestone] = useState(activity?.milestone ?? false)
  const [plannedStartDate, setPlannedStartDate] = useState(activity?.plannedStartDate ?? '')
  const [status, setStatus] = useState<ActivityStatus>(activity?.status ?? 'NOT_STARTED')

  const [workItemIds, setWorkItemIds] = useState<string[]>(
    activity?.workItems.map((w) => w.workItemId) ?? [],
  )
  const [drawingIds, setDrawingIds] = useState<string[]>(
    activity?.drawings.map((d) => d.drawingId) ?? [],
  )
  const [materialNeeds, setMaterialNeeds] = useState(
    activity?.materials.map((m) => ({ materialId: m.materialId, quantity: String(m.requiredQuantity) })) ?? [],
  )
  const [labourNeeds, setLabourNeeds] = useState(
    activity?.labour.map((l) => ({
      labourCategoryId: l.labourCategoryId,
      workers: String(l.requiredWorkers ?? 0),
    })) ?? [],
  )

  const [predecessorId, setPredecessorId] = useState('')
  const [dependencyType, setDependencyType] = useState<DependencyType>('FS')
  const [lagDays, setLagDays] = useState('0')
  const [error, setError] = useState<unknown>(null)

  const saving = create.isPending || update.isPending
  const blocked = saving || code.trim().length === 0 || name.trim().length < 2

  async function save() {
    setError(null)
    const body = {
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      workPackageId: workPackageId || undefined,
      durationDays: milestone ? 0 : Number(durationDays || 0),
      milestone,
      plannedStartDate: plannedStartDate || null,
      status,
      workItemIds,
      drawingIds,
      materials: materialNeeds.map((m) => ({
        materialId: m.materialId,
        requiredQuantity: m.quantity === '' ? 0 : Number(m.quantity),
      })),
      labour: labourNeeds.map((l) => ({
        labourCategoryId: l.labourCategoryId,
        requiredWorkers: l.workers === '' ? 0 : Number(l.workers),
      })),
    }
    try {
      if (activity) {
        await update.mutateAsync({ id: activity.id, body })
      } else {
        await create.mutateAsync(body)
      }
      onClose()
    } catch (err) {
      setError(err)
    }
  }

  async function addDependency() {
    if (!activity || !predecessorId) return
    setError(null)
    try {
      await link.mutateAsync({
        successorId: activity.id,
        predecessorId,
        dependencyType,
        lagDays: Number(lagDays || 0),
      })
      setPredecessorId('')
      setLagDays('0')
    } catch (err) {
      setError(err)
    }
  }

  const otherActivities = activities.filter((a) => a.id !== activity?.id)
  const existingLinks = dependencies.filter((d) => d.successorId === activity?.id)

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{activity ? `Edit ${activity.code}` : 'New activity'}</DialogTitle>
      <DialogContent dividers>
        {Boolean(error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {describeError(error)}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, mb: 2 }}>
          <TextField
            label="Code"
            required
            value={code}
            disabled={Boolean(activity)}
            helperText={activity ? 'Set once and not editable' : 'e.g. STR-03'}
            onChange={(e) => setCode(e.target.value)}
          />
          <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} />
        </Box>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, mb: 2 }}>
          <TextField
            label="Duration (days)"
            type="number"
            value={milestone ? 0 : durationDays}
            disabled={milestone}
            helperText={milestone ? 'A milestone has no duration' : 'Calendar days'}
            onChange={(e) => setDurationDays(e.target.value)}
          />
          <TextField
            label="Cannot start before"
            type="date"
            value={plannedStartDate}
            onChange={(e) => setPlannedStartDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Optional hard constraint"
          />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as ActivityStatus)}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s.replace('_', ' ').toLowerCase()}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 1 }}>
          <TextField
            select
            label="Work package"
            value={workPackageId}
            onChange={(e) => setWorkPackageId(e.target.value)}
          >
            <MenuItem value="">Not set</MenuItem>
            {(workPackages.data ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={milestone} onChange={(e) => setMilestone(e.target.checked)} />}
            label="This is a milestone, not work"
          />
        </Box>

        <TextField
          label="Description"
          fullWidth
          multiline
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }}>
          <Chip label="What this activity is joined to" size="small" />
        </Divider>

        <MultiSelect
          label="BOQ lines — where progress comes from"
          value={workItemIds}
          onChange={setWorkItemIds}
          options={(workItems.data ?? []).map((w) => ({
            id: w.id,
            primary: `${w.code} — ${w.description}`,
            secondary: `${w.totalPlannedQuantity} ${w.uomCode ?? ""}`,
          }))}
          empty="This site has no BOQ lines yet."
        />

        <MultiSelect
          label="Drawings needed before this can start"
          value={drawingIds}
          onChange={setDrawingIds}
          options={(drawings.data ?? []).map((d) => ({
            id: d.id,
            primary: `${d.drawingNumber} — ${d.title}`,
            secondary: d.status.toLowerCase(),
          }))}
          empty="No drawings on this site's register yet."
        />

        <QuantityList
          label="Materials needed"
          rows={materialNeeds.map((m) => ({ id: m.materialId, value: m.quantity }))}
          options={(materials.data?.content ?? []).map((m) => ({
            id: m.id,
            label: `${m.code} — ${m.name}`,
            suffix: m.uomCode,
          }))}
          valueLabel="Quantity"
          onChange={(rows) =>
            setMaterialNeeds(rows.map((r) => ({ materialId: r.id, quantity: r.value })))
          }
        />

        <QuantityList
          label="Trades needed"
          rows={labourNeeds.map((l) => ({ id: l.labourCategoryId, value: l.workers }))}
          options={(labourCategories.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
          valueLabel="Workers"
          onChange={(rows) =>
            setLabourNeeds(rows.map((r) => ({ labourCategoryId: r.id, workers: r.value })))
          }
        />

        {activity && (
          <>
            <Divider sx={{ my: 2 }}>
              <Chip label="Dependencies" size="small" />
            </Divider>

            {existingLinks.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Nothing comes before this yet.
              </Typography>
            ) : (
              existingLinks.map((existing) => (
                <Box
                  key={existing.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    After <strong>{existing.predecessorCode}</strong> ({existing.dependencyType}
                    {existing.lagDays !== 0
                      ? `, ${existing.lagDays > 0 ? '+' : ''}${existing.lagDays}d`
                      : ''}
                    )
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={unlink.isPending}
                    onClick={() => void unlink.mutateAsync(existing.id)}
                  >
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                </Box>
              ))
            )}

            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: '2fr 1.4fr 1fr 40px', alignItems: 'center' }}>
              <TextField
                select
                size="small"
                label="Comes after"
                value={predecessorId}
                onChange={(e) => setPredecessorId(e.target.value)}
              >
                <MenuItem value="">Pick an activity</MenuItem>
                {otherActivities.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Link"
                value={dependencyType}
                onChange={(e) => setDependencyType(e.target.value as DependencyType)}
              >
                {TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="number"
                label="Lag (days)"
                value={lagDays}
                onChange={(e) => setLagDays(e.target.value)}
              />
              <Button size="small" disabled={!predecessorId || link.isPending} onClick={() => void addDependency()}>
                Add
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              A negative lag overlaps the two. A link that would make a loop is refused.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {activity && (
          <Button
            color="error"
            sx={{ mr: 'auto' }}
            disabled={remove.isPending}
            onClick={async () => {
              if (window.confirm(`Remove ${activity.code}? Its dependencies go with it.`)) {
                await remove.mutateAsync(activity.id)
                onClose()
              }
            }}
          >
            Delete activity
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={blocked} onClick={() => void save()}>
          {saving ? 'Saving...' : activity ? 'Save changes' : 'Create activity'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** A tick-list of one kind of thing, kept compact because four of them share the dialog. */
function MultiSelect({
  label,
  value,
  onChange,
  options,
  empty,
}: {
  label: string
  value: string[]
  onChange: (next: string[]) => void
  options: { id: string; primary: string; secondary?: string }[]
  empty: string
}) {
  return (
    <TextField
      select
      fullWidth
      size="small"
      label={label}
      value={value}
      sx={{ mb: 2 }}
      slotProps={{
        select: {
          multiple: true,
          renderValue: (selected) => `${(selected as string[]).length} selected`,
        },
      }}
      onChange={(e) =>
        onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as unknown as string[]))
      }
      helperText={options.length === 0 ? empty : undefined}
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          <Checkbox size="small" checked={value.includes(option.id)} />
          <ListItemText primary={option.primary} secondary={option.secondary} />
        </MenuItem>
      ))}
    </TextField>
  )
}

/** A list of things with a number against each: materials by quantity, trades by headcount. */
function QuantityList({
  label,
  rows,
  options,
  valueLabel,
  onChange,
}: {
  label: string
  rows: { id: string; value: string }[]
  options: { id: string; label: string; suffix?: string }[]
  valueLabel: string
  onChange: (rows: { id: string; value: string }[]) => void
}) {
  const [pick, setPick] = useState('')
  const available = options.filter((o) => !rows.some((r) => r.id === o.id))

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {label}
      </Typography>

      {rows.map((row) => {
        const option = options.find((o) => o.id === row.id)
        return (
          <Box
            key={row.id}
            sx={{ display: 'grid', gridTemplateColumns: '1fr 140px 40px', gap: 1, mb: 1, alignItems: 'center' }}
          >
            <Typography variant="body2">{option?.label ?? row.id}</Typography>
            <TextField
              size="small"
              type="number"
              label={`${valueLabel}${option?.suffix ? ` (${option.suffix})` : ''}`}
              value={row.value}
              onChange={(e) =>
                onChange(rows.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)))
              }
            />
            <IconButton size="small" onClick={() => onChange(rows.filter((r) => r.id !== row.id))}>
              <DeleteOutlined fontSize="small" />
            </IconButton>
          </Box>
        )
      })}

      <TextField
        select
        size="small"
        fullWidth
        label={`Add ${label.toLowerCase()}`}
        value={pick}
        onChange={(e) => {
          if (e.target.value) {
            onChange([...rows, { id: e.target.value, value: '0' }])
          }
          setPick('')
        }}
      >
        <MenuItem value="">Pick one</MenuItem>
        {available.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  )
}
